import React, { useState, useRef } from 'react';
import { Button, Form, Message, Modal } from 'semantic-ui-react';
import styled from 'styled-components';
import PositionedModal from 'common/components/PositionedModal';
import { useToasts } from 'common/toast';
import { useSelector } from 'react-redux';
import { getCurrentOrganizationId } from 'common/selectors/auth';
import { H4 } from 'common/styles/page';
import { useNavigate } from 'react-router';

import { employeeImportApi } from '../api/employeeImportApi';

interface ImportMemberModalProps {
  mode: 'insert' | 'update';
  open: boolean;
  onClose: () => void;
}

const ImportMemberModal: React.FC<ImportMemberModalProps> = ({
  mode,
  open,
  onClose,
}) => {
  const { addToast } = useToasts();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const navigate = useNavigate();

  const organizationId = useSelector(getCurrentOrganizationId);

  const [importEmployees, { isLoading: isImporting }] = 
    employeeImportApi.useApiPortalV1EmployeeImportImportCreateMutation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
      setErrors([]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setErrors(['Please select a CSV file to import']);
      return;
    }

    setIsUploading(true);
    setErrors([]);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const result = await importEmployees({
        organizationId,
        //      mode: mode === 'insert' ? 1 : 2,
        mode: 0,
        body: formData as any,
      }).unwrap();

      if (result && typeof result === 'object' && 'rows' in result) {
        // Redirect to validation results page with results
        navigate('/app/import-review', {
          state: {
            results: result,
            mode,
            file: selectedFile,
          },
        });
      } else {
        // Optionally auto-import here or keep flow consistent by sending to review page anyway
        navigate('/app/import-review', {
          state: {
            results: result,
            mode,
            file: selectedFile,
          },
        });
      }
    } catch (error) {
      console.error('Validation failed:', error);
      setErrors(['Failed to validate file.']);
      addToast('Validation failed', { appearance: 'error', autoDismiss: true });
    } finally {
      setIsUploading(false);
    }
  };

  const isLoading = isUploading || isImporting;

  return (
    <PositionedModal open={open} onClose={onClose} size="small">
      <Modal.Header>{mode === 'insert' ? 'Import' : 'Update'} Members</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field>
            {mode === 'insert' ? (
              <H4>
                <span style={{ color: 'green', fontWeight: 700 }}>CREATE</span> new members in
                nuLogic and <span style={{ color: 'green', fontWeight: 700 }}>UPDATE</span> existing
                members.
              </H4>
            ) : (
              <H4>
                <span style={{ color: 'green', fontWeight: 700 }}>UPDATE</span> existing members in
                nuLogic and <span style={{ color: 'red', fontWeight: 700 }}>ARCHIVE</span> members
                not found in the import file.
              </H4>
            )}
          </Form.Field>
          <Form.Field>
            <FileUploadContainer>
              <FileInput
                id="member-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                ref={fileInputRef}
                disabled={isLoading || !organizationId}
              />
              <FileInputButton htmlFor="member-file" disabled={isLoading || !organizationId}>
                Choose file
              </FileInputButton>
              <FileName>{selectedFile?.name || 'No file selected'}</FileName>
            </FileUploadContainer>
          </Form.Field>

          {errors.length > 0 && (
            <Message negative>
              <Message.Header>Import Errors</Message.Header>
              <ErrorList>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ErrorList>
            </Message>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSubmit}
          loading={isLoading}
          disabled={!selectedFile || !organizationId}
        >
          Import
        </Button>
      </Modal.Actions>
    </PositionedModal>
  );
};

const FileUploadContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputButton = styled.label<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  padding: 0.75rem 1.2rem;
  background: #2185d0;
  color: white;
  font-weight: 700;
  border-radius: 0.35rem;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  border: none;
  text-align: center;
  user-select: none;
`;

const FileName = styled.span`
  color: #333;
  font-size: 0.95rem;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ErrorList = styled.ul`
  margin: 0;
  padding-left: 1.5em;
`;

export default ImportMemberModal;
